import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Modal, Select } from "./ui";
import { APP_FULL } from "../i18n";
import { Save, RefreshCw } from "lucide-react";

const GENERIC_KEYS = [
  "multilang", "activation", "backup", "terms_use", "privacy", "cookies",
  "disclaimer", "warranty", "law", "arbitration",
];
const APPS = ["family_tree", "holdings", "currency", "benefits", "master_enabler"];

function Area({ label, value, onChange, testid, rows = 4 }) {
  return (
    <label className="block w-full">
      <span className="mb-1 block text-xs font-medium opacity-70">{label}</span>
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]" />
    </label>
  );
}

export default function ManualEditorModal({ open, onClose }) {
  const [src, setSrc] = useState(null);
  const [tab, setTab] = useState("sections");
  const [appKey, setAppKey] = useState("family_tree");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMsg("");
      client.get("/manual/source").then((r) => setSrc(r.data)).catch((e) => setMsg(apiErr(e)));
    }
  }, [open]);

  if (!open) return null;

  const setGeneric = (k, v) => setSrc((s) => ({ ...s, generic: { ...s.generic, [k]: v } }));
  const setTitle = (k, v) => setSrc((s) => ({ ...s, section_titles: { ...s.section_titles, [k]: v } }));
  const setApp = (k, field, v) => setSrc((s) => ({ ...s, apps: { ...s.apps, [k]: { ...s.apps[k], [field]: v } } }));

  const save = async () => {
    setBusy(true); setMsg("Saving & notifying runners…");
    try {
      const apps = {};
      APPS.forEach((a) => {
        apps[a] = {
          title: src.apps[a].title,
          overview: src.apps[a].overview,
          features: (src.apps[a].features || []),
        };
      });
      const { data } = await client.put("/manual/source", {
        generic: src.generic, section_titles: src.section_titles, apps, notify: true,
      });
      setMsg(`Saved as version ${data.version} (${data.last_modified.slice(0, 10)}). Notified ${data.notified.recipients} runner(s).`);
      setSrc((s) => ({ ...s, version: data.version, last_modified: data.last_modified }));
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const regen = async () => {
    setBusy(true); setMsg("Re-generating 12-language translations…");
    try {
      const { data } = await client.post("/manual/regenerate-translations");
      setMsg(`Translated: ${data.translated.join(", ") || "none"}.${data.failed.length ? ` Failed: ${data.failed.length}.` : ""}`);
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit User Manual, Terms & Privacy" testid="manual-editor-modal" wide>
      {!src ? <div className="text-sm opacity-60">Loading…</div> : (
        <div className="space-y-4">
          <div className="text-xs opacity-60">Editing the English source. Version {src.version} · last modified {src.last_modified?.slice(0, 10)}. Saving bumps the version, updates the date, and emails/SMS-notifies all runners.</div>

          <div className="flex gap-2">
            <button onClick={() => setTab("sections")} data-testid="me-tab-sections"
              className={`rounded-md px-3 py-1.5 text-sm ${tab === "sections" ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>Shared sections</button>
            <button onClick={() => setTab("apps")} data-testid="me-tab-apps"
              className={`rounded-md px-3 py-1.5 text-sm ${tab === "apps" ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>Per-application</button>
          </div>

          {tab === "sections" && (
            <div className="space-y-4">
              {GENERIC_KEYS.map((k) => (
                <div key={k} className="space-y-2 rounded-lg border border-black/10 dark:border-white/10 p-3">
                  <input value={src.section_titles[k] || ""} onChange={(e) => setTitle(k, e.target.value)} data-testid={`me-title-${k}`}
                    className="w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-sm font-medium" />
                  <Area label="Body" value={src.generic[k]} onChange={(v) => setGeneric(k, v)} testid={`me-body-${k}`} rows={k === "backup" || k === "terms_use" || k === "privacy" || k === "cookies" ? 7 : 4} />
                </div>
              ))}
            </div>
          )}

          {tab === "apps" && (
            <div className="space-y-3">
              <Select label="Application" value={appKey} onChange={(e) => setAppKey(e.target.value)} data-testid="me-app-select">
                {APPS.map((a) => <option key={a} value={a}>{APP_FULL[a] || a}</option>)}
              </Select>
              <Area label="Overview" value={src.apps[appKey].overview} onChange={(v) => setApp(appKey, "overview", v)} testid="me-app-overview" rows={5} />
              <Area label="Key features (one per line)" rows={6}
                value={(src.apps[appKey].features || []).join("\n")}
                onChange={(v) => setApp(appKey, "features", v.split("\n").map((x) => x.trim()).filter(Boolean))}
                testid="me-app-features" />
            </div>
          )}

          {msg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2 text-sm" data-testid="me-msg">{msg}</div>}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={regen} disabled={busy} data-testid="me-regen"><RefreshCw size={15} /> Re-translate 15 languages</Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button onClick={save} disabled={busy} data-testid="me-save"><Save size={15} /> Save & notify runners</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
